import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { Star, Check, X, Trash2, Loader } from 'lucide-react'
import { formatDate } from '../../lib/calculations'
import { authedFetch } from '../../lib/authedFetch'

export default function ReviewsTab({ addToast }) {
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterRating, setFilterRating] = useState(0)
  const [filterStatus, setFilterStatus] = useState('all')
  const [actionLoading, setActionLoading] = useState(null)

  const fetchReviews = () => {
    setLoading(true)
    authedFetch('/api/reviews')
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setReviews(data) })
      .catch(err => {
        console.warn('[Admin] fetchReviews failed:', err?.message)
        Sentry.captureException(err, { extra: { fn: 'Admin.fetchReviews' } })
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchReviews() }, [])

  const updateStatus = async (id, status) => {
    setActionLoading(id)
    try {
      const res = await authedFetch('/api/reviews', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status }),
      })
      if (!res.ok) throw new Error('Failed')
      setReviews(prev => prev.map(r => r.id === id ? { ...r, status } : r))
      addToast?.(`Review ${status}`, status === 'approved' ? 'success' : 'info')
    } catch {
      addToast?.('Failed to update review', 'error')
    } finally {
      setActionLoading(null)
    }
  }

  const deleteReview = async (id) => {
    if (!confirm('Delete this review permanently?')) return
    setActionLoading(id)
    try {
      const res = await authedFetch('/api/reviews', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      })
      if (!res.ok) throw new Error('Failed')
      setReviews(prev => prev.filter(r => r.id !== id))
      addToast?.('Review deleted', 'success')
    } catch {
      addToast?.('Failed to delete review', 'error')
    } finally {
      setActionLoading(null)
    }
  }

  const filtered = reviews.filter(r => {
    if (filterStatus !== 'all' && (r.status || 'pending') !== filterStatus) return false
    if (filterRating && r.rating !== filterRating) return false
    if (search) {
      const s = search.toLowerCase()
      return r.product_name?.toLowerCase().includes(s) || r.business?.toLowerCase().includes(s) || r.reviewer_name?.toLowerCase().includes(s)
    }
    return true
  })

  const avgRating = reviews.length ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) : '—'
  const pendingCount = reviews.filter(r => (r.status || 'pending') === 'pending').length

  const statusColor = (s) => {
    if (s === 'approved') return 'text-green-400 bg-green-400/10'
    if (s === 'rejected') return 'text-red-400 bg-red-400/10'
    return 'text-yellow-400 bg-yellow-400/10'
  }

  if (loading) return <div className="py-12 text-center text-gray-400"><Loader size={24} className="animate-spin mx-auto" /></div>

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-white">{reviews.length}</p>
          <p className="text-xs text-gray-400">Total Reviews</p>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-yellow-400">{pendingCount}</p>
          <p className="text-xs text-gray-400">Pending</p>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-yellow-400">{avgRating}</p>
          <p className="text-xs text-gray-400">Avg Rating</p>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-400">{reviews.filter(r => r.rating >= 4).length}</p>
          <p className="text-xs text-gray-400">Positive (4-5)</p>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-red-400">{reviews.filter(r => r.rating <= 2).length}</p>
          <p className="text-xs text-gray-400">Negative (1-2)</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search product, business, reviewer..."
          className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white flex-1 max-w-sm"
        />
        <div className="flex gap-1">
          {['all', 'pending', 'approved', 'rejected'].map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(filterStatus === s ? 'all' : s)}
              className={`px-3 py-1.5 rounded text-xs font-medium capitalize ${
                filterStatus === s ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {[0,1,2,3,4,5].map(r => (
            <button
              key={r}
              onClick={() => setFilterRating(filterRating === r ? 0 : r)}
              className={`px-3 py-1.5 rounded text-xs font-medium ${
                filterRating === r ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              {r === 0 ? 'All' : `${r}★`}
            </button>
          ))}
        </div>
        <span className="text-gray-500 text-sm">{filtered.length} reviews</span>
      </div>

      {/* Reviews table */}
      {filtered.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No reviews found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700 text-gray-400 text-left">
                <th className="py-2 pr-4">Product</th>
                <th className="py-2 pr-4">Business</th>
                <th className="py-2 pr-4">Rating</th>
                <th className="py-2 pr-4">Comment</th>
                <th className="py-2 pr-4">Reviewer</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Date</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const st = r.status || 'pending'
                return (
                  <tr key={r.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                    <td className="py-2.5 pr-4 font-medium text-white">{r.product_name}</td>
                    <td className="py-2.5 pr-4 text-gray-300">{r.business}</td>
                    <td className="py-2.5 pr-4">
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} size={12} className={s <= r.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'} />
                        ))}
                      </div>
                    </td>
                    <td className="py-2.5 pr-4 text-gray-300 max-w-xs truncate">{r.comment || '—'}</td>
                    <td className="py-2.5 pr-4 text-gray-400">{r.reviewer_name}</td>
                    <td className="py-2.5 pr-4">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${statusColor(st)}`}>{st}</span>
                    </td>
                    <td className="py-2.5 pr-4 text-gray-500 text-xs whitespace-nowrap">{formatDate(r.created_at)}</td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-1">
                        {st !== 'approved' && (
                          <button
                            onClick={() => updateStatus(r.id, 'approved')}
                            disabled={actionLoading === r.id}
                            className="p-1.5 rounded bg-green-500/10 text-green-400 hover:bg-green-500/20 disabled:opacity-50"
                            title="Approve"
                          >
                            <Check size={14} />
                          </button>
                        )}
                        {st !== 'rejected' && (
                          <button
                            onClick={() => updateStatus(r.id, 'rejected')}
                            disabled={actionLoading === r.id}
                            className="p-1.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-50"
                            title="Reject"
                          >
                            <X size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => deleteReview(r.id)}
                          disabled={actionLoading === r.id}
                          className="p-1.5 rounded bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-red-400 disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
